// 注文状況の確認ウィジェット。
//
// おちゃのこ各店のフリーページに直書きされていた約270行（ChatBan #46）を、配信物として引き取ったもの。
// 挙動は page/2511 と同一。ただし注文番号＋メールのフォームは持たない（下記）。
//
// 店側の書き方:
//   <div data-wu-widget="order-status" data-site="1"></div>
//   <script src="https://front.workup-system.com/w/storefront.js" defer></script>
//
// ★フォーム経路を廃止した理由（2026-08-20）
//   このページのURLを知る手段は Guid 付きのメールしかない。もともとフォーム経路だけだったところに
//   利便性で Guid 経路を足した経緯で、いまは Guid 経由が主。
//   一方フォーム経路は、注文番号が連番のためメールアドレスが1つ分かれば総当たりで他人の注文を引ける。
//   Guid（128bit）に一本化すれば、その手口ごと消える。到達手段が元々メールだけなので導線も失わない。

import type { WidgetContext } from '../../shared/registry'
import { injectStyleOnce } from '../../shared/style'
import { css } from './style'
import { html } from './template'
import { fetchStatus, type StatusResponse } from './api'
import { copyText, esc, fmtMD, fmtUpdated, qparam, safeUrl } from './format'

// ★相対パスにしてはいけない。ウィジェットが動くのは店のページ（www.fukuro.in など）の中なので、
//   '/api/order-status' と書くと店のドメインを叩いて 404 になる。
//   配信元が同じでも「店のページから見れば別オリジン」であることを見落としやすい。
const DEFAULT_API = 'https://front.workup-system.com/api/order-status'

function orderStatus(el: HTMLElement, _ctx: WidgetContext): void {
  injectStyleOnce('order-status', css)

  const api = el.dataset.api || DEFAULT_API

  el.classList.add('opptrack')
  el.innerHTML = html

  // 要素はウィジェットのルート配下から引く。同じページに複数載っても混線しない。
  const q = <T extends HTMLElement>(cls: string): T => el.querySelector<T>('.' + cls)!

  const lead = q('js-lead')
  const loading = q('js-loading')
  const result = q('js-result')
  const errorBox = q('js-error')
  const refreshWrap = q('js-refresh-wrap')
  const refreshBtn = q<HTMLButtonElement>('js-refresh')
  const steps = q('js-rsteps')

  function renderTrack(tk: StatusResponse['tracking']): string {
    if (!tk || !tk.numbers || !tk.numbers.length) return ''

    let anyCopy = false
    const rows = tk.numbers
      .map((x, i) => {
        if (x.copy) anyCopy = true
        const lbl = tk.numbers.length > 1 ? `お荷物${i + 1}　` : ''
        // http(s) 以外は空にする。追跡先が開けなくなるだけで、番号の表示は残る。
        const url = safeUrl(x.url)
        const num = x.copy
          ? `<button type="button" class="ot-numlink js-copy" data-no="${esc(x.no)}" data-url="${esc(url)}">${esc(x.no)}</button>`
          : `<a class="ot-numlink" href="${esc(url)}" target="_blank" rel="noopener">${esc(x.no)}</a>`
        // 配送会社が判別できないときは carrier_name が空。会社名の行は出さず番号だけ見せる
        // （誤った会社名を出すより実害が小さい）。
        const carrier = tk.carrier_name ? `${esc(tk.carrier_name)}　` : ''
        return `<div class="ot-trow">${lbl}${carrier}お問い合わせ番号：${num}</div>`
      })
      .join('')

    // 追跡先が無い（会社が判別できない）ときは、開く案内も出さない。
    const note = !tk.carrier_name
      ? ''
      : `<div class="ot-t2note">番号を押すと${esc(tk.carrier_name)}の追跡ページが開きます` +
        (anyCopy ? '（番号はコピーされます。入力欄に貼り付けてご確認ください）' : '') +
        '</div>'

    return `<div class="ot-track2">${rows}${note}</div>`
  }

  function showError(text: string): void {
    result.classList.remove('ot-show')
    errorBox.textContent = text
    errorBox.classList.add('ot-show')
  }

  function render(d: StatusResponse): void {
    errorBox.classList.remove('ot-show')
    q('js-rhead').textContent = `注文番号 ${d.order_no} の状況`
    q('js-rorder').textContent = d.order_no
    q('js-rdate').textContent = d.order_date ? `ご注文日 ${d.order_date}` : ''
    q('js-rname').textContent = d.name ? `${d.name} のご注文です` : ''
    q('js-rpay').textContent = d.payment ? `お支払い方法：${d.payment}` : ''

    let body: string
    if (d.cancelled) {
      // キャンセル時はステッパーを描かず、steps[0].desc の文言だけを出す。
      const msg = (d.steps && d.steps[0] && d.steps[0].desc) || 'このご注文はキャンセルされました。'
      body = `<div class="ot-cancel">${esc(msg)}</div>`
    } else {
      const items = (d.steps || [])
        .map((s) => {
          const cls = s.state === 'done' ? 'ot-done' : s.state === 'current' ? 'ot-current' : 'ot-upcoming'
          const icon = s.state === 'done' ? '✓' : s.state === 'current' ? '▶' : ''
          const date = s.date ? `<span class="ot-vdate">${esc(fmtMD(s.date))}</span>` : ''
          const desc = s.desc ? `<div class="ot-vdesc">${esc(s.desc)}</div>` : ''
          const eta = s.eta ? `<div class="ot-veta">発送予定日：${esc(fmtMD(s.eta))}</div>` : ''
          // 追跡ボックスは "shipped" のステップにだけ差し込む。まだ来ていない段階では出さない。
          const track = s.key === 'shipped' && s.state !== 'upcoming' ? renderTrack(d.tracking) : ''
          return (
            `<div class="ot-vitem ${cls}"><div class="ot-vdot">${icon}</div>` +
            `<div class="ot-vlabel">${esc(s.label)}${date}</div>${desc}${eta}${track}</div>`
          )
        })
        .join('')
      body = `<div class="ot-vsteps">${items}</div>`
    }
    steps.innerHTML = body

    const up = fmtUpdated(d.last_updated)
    q('js-rupdated').textContent = up ? `照会時刻: ${up}` : ''
    q('js-live').textContent = 'この画面は押すたびに最新をリアルタイム表示しています（保存・キャッシュしません）。'

    steps.querySelectorAll<HTMLElement>('.js-copy').forEach((b) => {
      b.addEventListener('click', () => {
        copyText(b.getAttribute('data-no') || '')
        // 描画時に検証済みだが、DOM を触られた場合に備えて開く直前にもう一度見る。
        const url = safeUrl(b.getAttribute('data-url'))
        if (url) window.open(url, '_blank', 'noopener')
      })
    })

    reveal(result)
  }

  /** 枠を下に伸ばしてから中身を出す。伸びと中身を同時にやると瞬間的に入れ替わって見える。 */
  function reveal(el: HTMLElement): void {
    el.classList.add('ot-show', 'ot-grow')
    const h = el.scrollHeight
    el.style.height = '0px'
    // 同じフレームで目標値を入れると transition が走らないので次フレームまで待つ
    requestAnimationFrame(() => { el.style.height = h + 'px' })
    window.setTimeout(() => {
      el.style.height = ''
      el.classList.remove('ot-grow')
      el.classList.add('ot-fadein')
      window.setTimeout(() => el.classList.remove('ot-fadein'), 500)
    }, 420)
  }

  /** 照会中の文言を消し切ってから次を出す。 */
  function fadeOutLoading(): Promise<void> {
    if (!loading.classList.contains('ot-show')) return Promise.resolve()
    loading.classList.add('ot-out')
    return new Promise((done) => {
      window.setTimeout(() => {
        loading.classList.remove('ot-show', 'ot-out')
        done()
      }, 220)
    })
  }

  function query(guid: string, onDone?: () => void): void {
    result.classList.remove('ot-show')
    errorBox.classList.remove('ot-show')
    loading.classList.add('ot-show')

    fetchStatus(api, { g: guid })
      .then(
        (j) =>
          j && j.ok
            ? () => render(j)
            : () => showError('ご注文が見つかりませんでした。ご注文確認メールに記載のリンクからお開きください。'),
        () => () => showError('通信エラーが発生しました。時間をおいて再度お試しください。'),
      )
      .then((apply) => fadeOutLoading().then(apply))
      .then(() => { onDone?.() })
  }

  const guid = qparam('g')

  // URLを知る手段は Guid 付きのメールしかないので、通常はここに入る。
  if (guid) {
    refreshWrap.style.display = 'block'
    query(guid)
  } else {
    // リンクの一部だけをコピーした等で Guid が欠けた場合。案内だけ出す。
    loading.classList.remove('ot-show')
    lead.textContent = 'ご注文確認メールに記載のリンクから、このページをお開きください。'
  }

  refreshBtn.addEventListener('click', () => {
    refreshBtn.disabled = true
    refreshBtn.textContent = '更新中…'
    query(guid, () => {
      refreshBtn.disabled = false
      refreshBtn.textContent = '最新の状況に更新する'
    })
  })
}

// 動的 import で読まれる。名前との対応は shared/registry.ts の LOADERS にある。
export default orderStatus
